export class OpenAIError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'OpenAIError';
  }
}

export function handleOpenAIError(error: any): never {
  console.error('OpenAI API Error:', error);

  // Handle Supabase Edge Function errors
  if (error?.message?.includes('Failed to fetch') || 
      error?.message?.includes('Failed to send a request to the Edge Function')) {
    throw new OpenAIError('Edge Function に接続できません。Supabase の設定を確認してください。');
  }

  // Handle Edge Function configuration errors
  if (error?.error === 'OpenAI API key not configured' || 
      error?.details?.includes('OPENAI_API_KEY environment variable is missing')) {
    throw new OpenAIError('OpenAI API キーが設定されていません。Supabase の Edge Functions 設定で OPENAI_API_KEY 環境変数を設定してください。');
  }

  // Handle specific API errors from Edge Function response
  if (error?.error === 'OpenAI API request error') {
    throw new OpenAIError('画像の解析に失敗しました。別の画像を試してください。');
  }

  if (error?.error === 'Rate limit exceeded') {
    throw new OpenAIError('APIの利用制限に達しました。しばらく待ってから再度お試しください。');
  }

  if (error?.error === 'Insufficient quota') {
    throw new OpenAIError('OpenAI APIの利用制限に達しました。システム管理者に連絡してください。');
  }

  if (error?.error === 'credit_not_enough' || error?.message === 'credit_not_enough') {
    throw new OpenAIError('AIクレジットが不足しています。クレジットを購入するか、プランをアップグレードしてください。');
  }

  if (error?.message?.includes('AIクレジットが不足')) {
    throw new OpenAIError('AIクレジットが不足しています。クレジットを購入するか、プランをアップグレードしてください。');
  }

  if (error?.error === 'information_not_enough') {
    throw new OpenAIError('こちらの画像や情報のみでは、タイトルの作成が難しいです。');
  }

  // Handle direct OpenAI API errors (if any slip through)
  if (error?.error?.type === 'invalid_request_error') {
    if (error?.error?.message?.includes('model')) {
      throw new OpenAIError('申し訳ありませんが、現在画像解析サービスをご利用いただけません。システムの更新をお待ちください。');
    }
    if (error?.error?.message?.includes('size')) {
      throw new OpenAIError('画像サイズが大きすぎます。より小さい画像を使用してください。');
    }
    if (error?.error?.message?.includes('deprecated')) {
      throw new OpenAIError('申し訳ありませんが、現在システムのアップデート中です。しばらくお待ちください。');
    }
    if (error?.error?.message?.includes('quota')) {
      throw new OpenAIError('APIの利用制限に達しました。しばらく待ってから再度お試しください。');
    }
    if (error?.error?.message?.includes('content type')) {
      throw new OpenAIError('画像の形式が正しくありません。別の画像を試してください。');
    }
    if (error?.error?.message?.includes('Invalid content type')) {
      throw new OpenAIError('申し訳ありませんが、現在画像解析サービスをご利用いただけません。システムの更新をお待ちください。');
    }
    throw new OpenAIError('画像の解析に失敗しました。別の画像を試してください。');
  }

  // Handle rate limits
  if (error?.error?.code === 'rate_limit_exceeded') {
    throw new OpenAIError('APIの利用制限に達しました。しばらく待ってから再度お試しください。');
  }

  // Handle authentication errors
  if (error?.error?.code === 'invalid_api_key') {
    throw new OpenAIError('APIキーが無効です。システム管理者に連絡してください。');
  }

  // Handle context length errors
  if (error?.error?.code === 'context_length_exceeded') {
    throw new OpenAIError('画像が多すぎます。より少ない枚数で試してください。');
  }

  // Handle timeouts
  if (error?.error?.code === 'timeout') {
    throw new OpenAIError('応答がタイムアウトしました。再度お試しください。');
  }

  // Handle quota errors
  if (error?.error?.code === 'insufficient_quota') {
    throw new OpenAIError('APIの利用制限に達しました。システム管理者に連絡してください。');
  }

  // Handle network errors
  if (error instanceof TypeError && error.message.includes('fetch')) {
    throw new OpenAIError('ネットワークエラーが発生しました。インターネット接続を確認してください。');
  }

  // Handle JSON parsing errors
  if (error?.message?.includes('JSON')) {
    throw new OpenAIError('サーバーからの応答を解析できませんでした。再度お試しください。');
  }

  // Default error with more helpful message
  const errorMessage = error?.error || error?.message || 'タイトルの生成に失敗しました';
  throw new OpenAIError(`${errorMessage}。しばらく待ってから再度お試しください。`);
}